import { useTabsCtx } from 'app/components/management/apps/tabs-page-layout';
import { generatePath, useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { PaginationResponseType } from 'app/console-api';
import { ErrorResult, Pagination } from 'app/organizations-api/types';
import { APPS_TASKS_VIEW_PATH, SIGN_IN_PATH } from 'app/route/paths';
import { HttpError } from 'app/components/utils/http-error';
import { AUTH_TOKEN, customErrorMessage, statusCode } from 'app/constants';
import { PageContainer } from 'app/components/common/common-styles';
import { PreloadTableWindow } from 'app/components/common/preload-table-window/preload-table-window';
import { SvgIcon, TableRow, TableRowType } from '@make-software/cspr-design';
import NoFound from 'assets/logos/no-found.svg';
import Table from 'app/components/common/table/table';
import BaseTableHeader from 'app/components/common/base-table/base-table-header';
import { getTaskLogs, TaskLogsListItemResponse } from 'app/console-api/tasks/tasks-logs';
import { TasksLogsItem } from 'app/components/management/apps/tasks/logs/tasks-logs-item';

export const TasksLogsPage = () => {
  const { appID, setBreadcrumbs } = useTabsCtx();
  const { id } = useParams<{ id: string }>();
  const [logs, setLogs] = useState<PaginationResponseType<TaskLogsListItemResponse> | null>(null);
  const [pagination, setPagination] = useState<Pagination>({ currentPage: 1, pageSize: 5 });
  const [loading, setLoading] = useState<boolean>(true);
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);

  React.useEffect(() => {
    if (!id) return;
    setBreadcrumbs([
      {
        label: id,
        to: generatePath(APPS_TASKS_VIEW_PATH, { appID, id: id ?? '' })
      },
      {
        label: 'Action logs'
      }
    ]);
    return () => setBreadcrumbs([]);
  }, [id, appID, setBreadcrumbs]);

  useEffect(() => {
    setLoading(true);
    if (!id) {
      return;
    }
    getTaskLogs(appID, id, pagination)
      .then((response) => {
        setLogs(response);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to fetch deals logs:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [pagination, id]);

  const handlePageActions = (page: number, limit: number) => {
    if (pagination.pageSize != limit) {
      page = 1;
    }
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
      pageSize: limit
    }));
  };

  const noLogs = !logs?.data && !loading && !unexpectedError;

  return (
    <PageContainer>
      {noLogs ? (
        <PreloadTableWindow
          icon={<SvgIcon src={NoFound} height={120} width={224} />}
          title="No logs yet"
        />
      ) : (
        <Table
          loading={loading}
          data={logs?.data || []}
          pageCount={logs?.page_count || 1}
          error={unexpectedError}
          pageSize={pagination.pageSize}
          currentPage={pagination.currentPage}
          handlePageActions={handlePageActions}
          tableRowType={TableRowType.TextSingleLine}
          renderDataHeader={() => (
            <TableRow>
              <BaseTableHeader fixedWidthRem={35}>Timestamp</BaseTableHeader>
              <BaseTableHeader fixedWidthRem={30}>Action</BaseTableHeader>
              <BaseTableHeader fixedWidthRem={35}>User</BaseTableHeader>
            </TableRow>
          )}
          renderPaginatedData={(one) => {
            return one.map((log) => <TasksLogsItem key={log.logData.id} data={log} />);
          }}
        />
      )}
    </PageContainer>
  );
};
